import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmPopup from "@/app/common-hooks/gtm/use-gtm-popup";
import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { Routes } from "@/app/config/page-view-constants";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { CoverEdit, QuestionOne } from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import {
  saveQuestionDataBFF,
  sessionCheck,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState, store } from "@/store/store";
import { QuotePayload } from "@/types/quote/quote-data";

import { useCoverTypeContext } from "../provider/cover-type.provider";

function useCoverTypeHook() {
  const dispatch = useDispatch<typeof store.dispatch>();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const [isFooterLoading, setIsFooterLoading] = useState(false);
  const { loading } = quoteResponse;

  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(CoverEdit);
  const partnership = searchParams.get("partnership");
  const isBcuk = partnership === "bcuk";

  const { current } = useWizard();

  const [payload, setPayload] = useState<QuotePayload | null>(null);

  const { selectedAnswer, setSelectedAnswer, isCoverTypeModalOpen } =
    useCoverTypeContext();

  useEffect(() => {
    if (quoteResponse?.attributes?.tripDetails?.policyType) {
      if (quoteResponse?.attributes?.tripDetails.policyType === SINGLE_TRIP) {
        setSelectedAnswer(1);
      }
      if (
        quoteResponse?.attributes?.tripDetails.policyType === ANNUAL_MULTI_TRIP
      ) {
        setSelectedAnswer(2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteResponse]);

  function getPolicyTypeWithMapping(id: number): string {
    const answerToPolicyMap: Record<number, string> = {
      1: SINGLE_TRIP,
      2: ANNUAL_MULTI_TRIP,
    };
    return answerToPolicyMap[id];
  }

  const marketingChannelDetails = {
    marketingChannelData: window.location.href ?? "",
    referrer: window.document.referrer ?? "",
  };

  const handleSubmitButton = async () => {
    if (selectedAnswer === 0) return;
    try {
      setIsFooterLoading(true);

      const policyType = getPolicyTypeWithMapping(selectedAnswer);
      const isChangePolicyType =
        policyType !== quoteResponse.attributes.tripDetails?.policyType;

      const tripDetails = {
        ...quoteResponse.attributes.tripDetails,
        policyType,
        ...(isChangePolicyType && { startDate: undefined, endDate: undefined }),
      };

      const payloadData: QuotePayload = {
        attributes: {
          ...quoteResponse.attributes,
          marketingChannelDetails:
            !editPage && marketingChannelDetails
              ? marketingChannelDetails
              : quoteResponse?.attributes?.marketingChannelDetails,
          tripDetails,
          partnerCode:
            isBcuk && (current.brandSpecificParams?.isBCUKEnabled as boolean)
              ? "BCUK"
              : quoteResponse?.attributes?.partnerCode,
        },
      };

      setPayload(payloadData);
      dispatch(setQuestionData(payloadData));
      if (!quoteResponse?.referenceId) {
        const questionSaveData = {
          type: "IntermediateData",
          channel: quoteResponse?.channel,
          ...payloadData,
        };
        const questionData = dispatch(
          saveQuestionDataBFF({
            payload: { ...questionSaveData, viewPage: Routes.CoverType },
            dispatch,
            router,
          })
        );
        const originalPromiseResult = unwrapResult(await questionData);
        if (originalPromiseResult) {
          router.push(current.next ?? "");
        }
      } else {
        dispatch(sessionCheck({ dispatch, router }));
        let path = current.next ?? "";
        if (editPage) {
          // Check if user came from quote-review (sessionStorage is set)
          const editSection = getQuoteReviewSummaryEditSection();

          if (editSection === "cover") {
            // User came from quote-review, use new flow
            path = current.edges.coverEditQuoteSummaryNext ?? "";
          } else {
            // User came from quote page, use old flow
            path = current.edges.toNextCoverEdit ?? "";
          }
        }
        router.push(path);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving question data:", error);
      setIsFooterLoading(false);
    }
  };

  // gtm
  useGtmQuestionSet({
    pageTitle: QuestionOne,
    tripType: payload?.attributes?.tripDetails?.policyType ?? "",
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    isSubmitBtnClicked: payload !== null,
  });

  useGtmPopup({
    tripType: QuestionOne,
    isPopupOpen: isCoverTypeModalOpen,
    popupName: " Single Cover Popup",
  });

  return {
    loading,
    isFooterLoading,
    handleSubmitButton,
  };
}

export default useCoverTypeHook;
